import produce from 'immer';
import * as zeroCreateStore from 'redux-zero';
import { connect as devtoolsMiddleware } from 'redux-zero/devtools';
import { applyMiddleware } from 'redux-zero/middleware';
import { bindActions } from 'redux-zero/utils';
import { OmitFirstArg } from '../App.types';
import { Locations, WidgetTab } from '../common';
import {
  DeepPartial,
  isDevEnv,
  isEmpty,
  isPresent,
  isServer,
  mergeDeep,
  MergeOptions,
  overWriteArray,
  produceMerge,
} from '../utils';
import { Actions, actions } from './actions';
import {
  persistMiddleware,
  readInitialAmState,
  readInitialState,
  sanitizeSelection,
  writeBookingState,
} from './persistMiddleware';
import { sideEffects } from './sideEffects';
import { AmSelectionState, BookingState, initialAmSelectionState, initialState, SelectionState } from './state';
import { destinationCode, locationsAreSame, originCode } from './stateUtils';
import { storage } from './storage';
import {
  ActionsFactory,
  LifecycleEvent,
  Middleware,
  SideEffect,
  SideEffectAction,
  SideEffectGenerator,
  Store,
  StoreActions,
} from './types';
import { WidgetTestVariant } from '../config';

export const STORAGE_KEY = 'BookingWidget';

const MERGE_OPTIONS: MergeOptions = {
  overwriteWithEmpty: false,
  arrayMerge: overWriteArray,
};

// == Lifecycle ================================================================

const STORE_INTERVAL = 60000; // 1min

export interface LifecycleActions<T> {
  $storeInit: (initState: DeepPartial<T>) => any;
  $storeInterval: () => any;
  $storeShutdown: () => any;
}

const $storeInit = (state: BookingState, initState: DeepPartial<BookingState>) => produceMerge(state, initState);
$storeInit.id = LifecycleEvent.INIT;
const $storeInterval = () => {}; // eslint-disable-line
$storeInterval.id = LifecycleEvent.INTERVAL;
const $storeShutdown = () => {}; // eslint-disable-line
$storeShutdown.id = LifecycleEvent.SHUTDOWN;
export const lifecycleActions: ActionsFactory<BookingState, LifecycleActions<BookingState>> = () => ({
  $storeInit,
  $storeInterval,
  $storeShutdown,
});

const lifecycleActionIds = [$storeInit.id, $storeInterval.id, $storeShutdown.id];

// == Utilities ================================================================

const createStore = <T>(initialState: T, middlewares?: Middleware<T>[]): Store<T> => {
  if (isEmpty(middlewares)) {
    return (zeroCreateStore as any)(initialState);
  }
  return (zeroCreateStore as any)(initialState, applyMiddleware(...middlewares));
};

// == BookingStore =============================================================

export interface BookingStoreConfig {
  stateOverrides: DeepPartial<BookingState>;
  enablePersistence: boolean;
  testVariant: WidgetTestVariant;
}

export const createInitState = ({ stateOverrides: overrides, enablePersistence }: BookingStoreConfig): BookingState => {
  if (enablePersistence) {
    const stateFromStorage: Partial<BookingState> = readInitialState(storage.session, STORAGE_KEY);
    const sanitizedSelection: DeepPartial<SelectionState> = sanitizeSelection(
      stateFromStorage?.selection,
      overrides.profile,
      overrides.enableAward,
      overrides.enableAwardIfLoggedIn,
      overrides.enableAurinkomatkat,
      !(overrides.enableCompactWidget && overrides.isInitialRender)
    );
    const sanitizedStateFromStorage: DeepPartial<BookingState> = {
      ...stateFromStorage,
      currentTab: sanitizedSelection.activatedTab as WidgetTab,
      selection: sanitizedSelection,
    };
    const amSelectionsFromStorage = readInitialAmState(storage.session, STORAGE_KEY);

    const initialBookingState = mergeDeep(
      [initialState, sanitizedStateFromStorage ?? {}],
      MERGE_OPTIONS
    ) as BookingState;
    const initialAmSelections: AmSelectionState = {
      ...initialAmSelectionState,
      ...amSelectionsFromStorage,
    };

    const initState: BookingState = produce(initialBookingState, (state: BookingState) => {
      state.amState.amSelection = initialAmSelections;
    });

    const data = mergeDeep([initState, overrides as Partial<BookingState>], MERGE_OPTIONS);

    // If UX does not specifically pass dates, then clear any dates from session storage.
    if (overrides && overrides.selection && !overrides?.selection?.travelDates) {
      data.selection.travelDates = {};
    }

    return data;
  }

  return mergeDeep([initialState, overrides as Partial<BookingState>], MERGE_OPTIONS);
};

const bindEffects = <T>(
  effectsGenerators: ((store: Store<BookingState>) => SideEffect<T>)[],
  store: Store<BookingState>
): SideEffect<T>[] => effectsGenerators.map((effect) => effect(store));

const isLifecycleAction = (action: SideEffectAction<any>): boolean => {
  return lifecycleActionIds.includes(action.id as LifecycleEvent);
};

export const createSideEffectsMiddleWare =
  (sideEffectGenerators: SideEffectGenerator<BookingState>[]) =>
  (
    store: Store<BookingState>
  ): ((next: (action: SideEffectAction<any>) => BookingState) => (action: () => any) => any) => {
    const effects = bindEffects(sideEffectGenerators, store);

    return (next: (action: SideEffectAction<any>) => BookingState) => (action: SideEffectAction<any>) => {
      const previousState = store.getState();
      const actionResult = next(action);
      const nextState = store.getState();

      const isLFAction = isLifecycleAction(action);
      effects.forEach((effect) => {
        const lifeCycleEvent = effect.runOn?.find((e) => e.type === action.id);
        if (
          (isLFAction &&
            lifeCycleEvent &&
            (lifeCycleEvent.when ? lifeCycleEvent.when(previousState, nextState) : true)) ||
          (!isLFAction && effect.when(previousState, nextState))
        ) {
          effect.runSideEffects(nextState);
        }
      });

      return actionResult;
    };
  };

let updateActions: {
  mergePartialState: OmitFirstArg<StoreActions<BookingState, Actions>['mergePartialState'], Promise<void> | void>;
};

export const initBookingStore = ({
  enablePersistence,
  stateOverrides,
  testVariant,
}: BookingStoreConfig): Store<BookingState> => {
  if (isServer()) {
    const state: BookingState = mergeDeep([initialState, stateOverrides as Partial<BookingState>], MERGE_OPTIONS);
    return createStore(state);
  }

  const middlewares: Middleware<BookingState>[] = [];
  if (enablePersistence) {
    middlewares.push(persistMiddleware(storage.session, STORAGE_KEY));
  }

  middlewares.push(createSideEffectsMiddleWare(sideEffects));

  if (isDevEnv() && devtoolsMiddleware) {
    middlewares.push(devtoolsMiddleware(initialState));
  }

  const store: Store<BookingState> = createStore(initialState, middlewares);
  const initState: BookingState = createInitState({ stateOverrides, enablePersistence, testVariant });

  const boundLifecycleActions: LifecycleActions<BookingState> = bindActions(lifecycleActions, store);
  boundLifecycleActions.$storeInit(initState);

  updateActions = bindActions(actions, store);

  const interval = setInterval(boundLifecycleActions.$storeInterval, STORE_INTERVAL);
  store.shutdown = () => {
    boundLifecycleActions.$storeShutdown();
    clearInterval(interval);
  };

  return store;
};

/**
 * Writes widget's state updates to sessionStorage
 * @param updateSelection Partial selections update to be merged into current selections
 * @param overWrite Flag to overwrite the SelectionState with updateSelection instead of merge
 * @param updateStore Flag to update the store with the new selections
 */
export const updateState = (
  updateSelection: DeepPartial<SelectionState>,
  overWrite?: boolean,
  updateStore?: boolean
): void => {
  const sanitizedUpdates: DeepPartial<SelectionState> = sanitizeSelection(
    updateSelection,
    initialState.profile,
    initialState.enableAward,
    initialState.enableAwardIfLoggedIn,
    initialState.enableAurinkomatkat,
    true
  );
  const savedSelection: DeepPartial<BookingState> = readInitialState(storage.session, STORAGE_KEY);
  const baseSelection: DeepPartial<SelectionState> =
    !overWrite && isPresent(savedSelection) ? savedSelection.selection : initialState.selection;
  const mergedSelection: DeepPartial<SelectionState> = mergeDeep([baseSelection, sanitizedUpdates], MERGE_OPTIONS);
  const state: BookingState = mergeDeep(
    [initialState, { selection: mergedSelection } as Partial<BookingState>],
    MERGE_OPTIONS
  );

  // If the locations are duplicates after merging the updateSelection with the saved/initial selections
  // e.g. stored origin=HEL, updated destination=HEL, we give precedence for the update
  // removing the opposite location from the final selection
  // TODO: maybe put this into a separate function and return a new state rather than mutate it here
  if (locationsAreSame(state.selection.locations[state.selection.activeLocationIndex])) {
    const originUpdate: string = originCode(sanitizedUpdates?.locations as Locations);
    const destinationUpdate: string = destinationCode(sanitizedUpdates?.locations as Locations);

    if (isPresent(originUpdate)) {
      delete state.selection.locations[state.selection.activeLocationIndex].destination;
    } else if (isPresent(destinationUpdate)) {
      delete state.selection.locations[state.selection.activeLocationIndex].origin;
    }
  }

  writeBookingState(STORAGE_KEY, state);

  if (updateStore) {
    updateActions?.mergePartialState({
      selection: {
        ...state.selection,
      },
    });
  }
};
